/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldAlert, Users, Database, Zap, Settings, ChevronRight, ChevronLeft, LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import {
  DEV_TOOLS_ENABLED, fetchDevStatus, impersonateRole, fetchSmtpLog, countLocalIndexedDB,
  rememberImpersonation, clearImpersonation, getImpersonation,
} from '@/lib/devTools';
import { loadChains, saveChains, roleKeyForUser } from './devConstants';
import { RoleSwitcher, ChainsSection, DiffPlayground } from './RolesTab';
import { DataTab } from './DataTab';
import { GatewaysTab } from './GatewaysTab';
import { ConfigTab } from './ConfigTab';

const TAB_CLASS = 'flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3 mt-2';

/**
 * SuperAdminSidebar — right-side collapsible overlay that exposes every dev
 * helper we ship (role impersonation, DB seeding, gateway simulators, config
 * toggles). Rendered globally by App.jsx but hidden unless the current user
 * has the ``is_dev_admin`` flag AND the build was compiled with
 * ``REACT_APP_ENABLE_DEV_TOOLS=true``.
 *
 * Intentionally NOT styled to blend with the app — bold red "DEV MODE" banner
 * + monospace font make it impossible to confuse with the real UI.
 * Sections live in RolesTab / DataTab / GatewaysTab / ConfigTab.
 */
export const SuperAdminSidebar = () => {
  const { user, refreshUser } = useAuth() || {};
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState(null);
  const [idbCount, setIdbCount] = useState(null);
  const [smtpEntries, setSmtpEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [impersonation, setImpersonation] = useState(getImpersonation());
  const [chains, setChains] = useState(loadChains);
  const [runningChainId, setRunningChainId] = useState(null);
  const [diffLeft, setDiffLeft] = useState(null);
  const [diffRight, setDiffRight] = useState(null);

  const isDevAdmin = !!(user && (user.is_dev_admin || user.email === 'admin.test@sanitanexus.it') && DEV_TOOLS_ENABLED);

  const reloadStatus = useCallback(async () => {
    if (!isDevAdmin) return;
    try {
      const [s, idb, smtp] = await Promise.all([
        fetchDevStatus(),
        countLocalIndexedDB().catch(() => null),
        fetchSmtpLog(5).catch(() => ({ entries: [] })),
      ]);
      setStatus(s); setIdbCount(idb); setSmtpEntries(smtp.entries || []);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[dev-tools] reloadStatus:', e);
    }
  }, [isDevAdmin]);

  useEffect(() => { reloadStatus(); }, [reloadStatus, user?.id]);

  /** Esegue un'azione asincrona gestendo busy + toast di errore. */
  const withBusy = useCallback(async (fn, errorMsg) => {
    setBusy(true);
    try { return await fn(); }
    catch (e) { toast.error(e?.response?.data?.detail || errorMsg); return undefined; }
    finally { setBusy(false); }
  }, []);

  if (!DEV_TOOLS_ENABLED || !isDevAdmin) return null;

  const applyImpersonation = async (role) => {
    const res = await impersonateRole({ role });
    rememberImpersonation({ actor: user.email, target: res.user, at: new Date().toISOString() });
    setImpersonation({ actor: user.email, target: res.user });
    await refreshUser();
    return res;
  };

  const handleImpersonate = (role) => withBusy(async () => {
    const res = await applyImpersonation(role);
    toast.success(`Ora sei: ${res.user.email}`);
  }, 'Impersonation fallita');

  const handleReturnToAdmin = () => withBusy(async () => {
    await impersonateRole({ role: 'admin' });
    toast.info('Tornato al Super Admin');
    clearImpersonation(); setImpersonation(null);
    await refreshUser();
  }, 'Impossibile tornare a Super Admin: esegui logout e login di nuovo');

  const runChain = (chain) => {
    if (!chain?.steps?.length) return;
    setRunningChainId(chain.id);
    withBusy(async () => {
      for (const [idx, step] of chain.steps.entries()) {
        const res = await applyImpersonation(step.role);
        toast.success(`[${idx + 1}/${chain.steps.length}] ${res.user.email}`);
        await new Promise((r) => setTimeout(r, 700)); // lascia rehydratare la UI tra gli step
      }
      toast.info(`Sequenza "${chain.name}" completata (${chain.steps.length} step)`);
    }, 'Chain interrotta').finally(() => setRunningChainId(null));
  };

  const saveCurrentAsChain = () => {
    if (!impersonation?.target) { toast.error('Prima impersona almeno un utente'); return; }
    const name = window.prompt('Nome della sequenza:', `Chain del ${new Date().toLocaleDateString('it')}`);
    if (!name) return;
    const next = [...chains, { id: `custom-${Date.now()}`, name: name.slice(0, 60), steps: [{ role: roleKeyForUser(impersonation.target) }], icon: '⭐' }];
    setChains(next); saveChains(next);
    toast.success(`Sequenza "${name.slice(0, 60)}" salvata`);
  };

  const removeChain = (id) => {
    if (!window.confirm('Rimuovere questa sequenza?')) return;
    const next = chains.filter((c) => c.id !== id);
    setChains(next); saveChains(next);
  };

  const captureDiffSlot = (side) => {
    const roleKey = side === 'left' ? diffLeft?.role : diffRight?.role;
    if (!roleKey) { toast.error('Scegli un ruolo per lo slot'); return; }
    withBusy(async () => {
      const res = await impersonateRole({ role: roleKey });
      const { email, full_name, category, professional_role, is_admin } = res.user;
      const snapshot = { email, full_name, category, professional_role, is_admin };
      (side === 'left' ? setDiffLeft : setDiffRight)({ role: roleKey, snapshot });
      await impersonateRole({ role: 'admin' }); // la cattura cambia la sessione: torna subito admin
      await refreshUser();
    }, 'Snapshot fallito');
  };

  if (!open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[300] bg-red-600 hover:bg-red-700 text-white text-[10px] font-mono font-bold uppercase tracking-widest py-3 px-1.5 rounded-l-md shadow-lg flex flex-col items-center gap-2"
        data-testid="super-admin-toggle" aria-label="Apri Super Admin Dashboard"
      >
        <ShieldAlert className="h-4 w-4" />
        <span style={{ writingMode: 'vertical-rl' }}>DEV&nbsp;MODE</span>
        <ChevronLeft className="h-3 w-3" />
      </button>
    );
  }

  const impersonating = impersonation?.target && impersonation.target.email !== user?.email;

  return (
    <aside className="fixed right-0 top-0 h-screen w-full sm:w-[400px] z-[300] bg-background/95 backdrop-blur-xl border-l-4 border-red-600 shadow-2xl flex flex-col" data-testid="super-admin-sidebar" role="complementary">
      <header className="bg-red-600 text-white px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="h-4 w-4 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-xs font-bold uppercase tracking-widest">DEV MODE · Super Admin</div>
            <div className="text-[10px] opacity-90 truncate">{user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-7 w-7" onClick={reloadStatus} title="Ricarica stato" data-testid="super-admin-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-7 w-7" onClick={() => setOpen(false)} title="Nascondi" data-testid="super-admin-close">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {impersonating && (
        <div className="bg-amber-500/20 border-b border-amber-500/40 px-3 py-2 flex items-center justify-between gap-2">
          <div className="text-[11px] leading-snug"><span className="font-bold">Impersonando:</span> <span className="font-mono">{impersonation.target.email}</span></div>
          <Button size="sm" variant="outline" onClick={handleReturnToAdmin} data-testid="super-admin-return"><LogOut className="h-3 w-3 mr-1" /> Torna admin</Button>
        </div>
      )}

      <Tabs defaultValue="roles" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="grid grid-cols-4 mx-3 mt-3 h-9">
          <TabsTrigger value="roles" className="text-[11px] gap-1" data-testid="tab-roles"><Users className="h-3 w-3" />Ruoli</TabsTrigger>
          <TabsTrigger value="data" className="text-[11px] gap-1" data-testid="tab-data"><Database className="h-3 w-3" />Dati</TabsTrigger>
          <TabsTrigger value="gateways" className="text-[11px] gap-1" data-testid="tab-gateways"><Zap className="h-3 w-3" />Gateway</TabsTrigger>
          <TabsTrigger value="config" className="text-[11px] gap-1" data-testid="tab-config"><Settings className="h-3 w-3" />Config</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className={TAB_CLASS}>
          <RoleSwitcher busy={busy} status={status} onImpersonate={handleImpersonate} />
          <ChainsSection chains={chains} busy={busy} runningChainId={runningChainId} onRun={runChain} onSaveCurrent={saveCurrentAsChain} onRemove={removeChain} />
          <DiffPlayground busy={busy} diffLeft={diffLeft} diffRight={diffRight} setDiffLeft={setDiffLeft} setDiffRight={setDiffRight} onCapture={captureDiffSlot} />
        </TabsContent>
        <TabsContent value="data" className={TAB_CLASS}>
          <DataTab busy={busy} withBusy={withBusy} idbCount={idbCount} setIdbCount={setIdbCount} reloadStatus={reloadStatus} />
        </TabsContent>
        <TabsContent value="gateways" className={TAB_CLASS}>
          <GatewaysTab busy={busy} withBusy={withBusy} />
        </TabsContent>
        <TabsContent value="config" className={TAB_CLASS}>
          <ConfigTab busy={busy} withBusy={withBusy} status={status} smtpEntries={smtpEntries} setSmtpEntries={setSmtpEntries} />
        </TabsContent>
      </Tabs>

      <Separator />
      <footer className="px-3 py-1.5 text-[9px] font-mono text-muted-foreground flex items-center justify-between">
        <span>ENABLE_DEV_TOOLS=true</span>
        <span>{status?.app_env || 'dev'}</span>
      </footer>
    </aside>
  );
};

export default SuperAdminSidebar;
